import axios from 'axios';
import { createSlice, createAsyncThunk, miniSerializeError, PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { resetStore } from 'src/store';
import { getSchemeListByCompanyId } from 'src/api/eArchive/scheme';
import { SchemeResponse, ResultFailType } from 'src/types';

import { isDataAccessError } from '@itm/shared-frontend/lib/components/dataAccess';

type SchemeState = {
  isLoading: boolean;
  hasDataAccess: boolean;
  schemeList: SchemeResponse[];
  selectedSchemaId: string;
};

const serializeError = (e: unknown): SerializedError => {
  const name =
    e instanceof axios.AxiosError && isDataAccessError(e.response)
      ? ResultFailType.DataAccessValidation
      : ResultFailType.Default;
  return miniSerializeError({ name });
};

export const fetchSchemeListByCompanyAction = createAsyncThunk<SchemeResponse[], string>(
  'scheme/fetchSchemeListByCompany',
  async (companyId) => {
    const res = await getSchemeListByCompanyId(companyId);
    return res.data;
  },
  { serializeError },
);

const initialState: SchemeState = {
  isLoading: false,
  hasDataAccess: true,
  schemeList: [],
  selectedSchemaId: '',
};

const schemeSlice = createSlice({
  name: 'scheme',
  initialState,
  reducers: {
    selectScheme(state: SchemeState, action: PayloadAction<string>) {
      state.selectedSchemaId = action.payload;
    },
  },
  extraReducers: (builder) => {
    // SuperAdmin
    builder.addCase(fetchSchemeListByCompanyAction.pending, (state: SchemeState) => {
      state.isLoading = true;
      state.hasDataAccess = true;
    });
    builder.addCase(fetchSchemeListByCompanyAction.fulfilled, (state: SchemeState, action) => {
      state.isLoading = false;
      state.hasDataAccess = true;
      state.schemeList = action.payload;
    });
    builder.addCase(fetchSchemeListByCompanyAction.rejected, (state: SchemeState, action) => {
      state.hasDataAccess = action.error.name !== ResultFailType.DataAccessValidation;
      state.isLoading = false;
      state.schemeList = [];
      state.selectedSchemaId = '';
    });

    // Cleanup
    builder.addCase(resetStore, (state: SchemeState) => {
      Object.assign(state, initialState);
    });
  },
});

export const { selectScheme } = schemeSlice.actions;

export default schemeSlice.reducer;
